import { useEffect, useState } from "react";
import {
  createAnswerShare,
  deleteAnswerShare,
  fetchAnswerShare,
  patchAnswerShare,
} from "@/api/answerShare";
import { ApiError } from "@/api/client";
import { IconCheck, IconChevronDown, IconCopy } from "@/components/icons";
import {
  FieldLabel,
  TextInput,
  ToggleSwitch,
} from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import {
  defaultExpiresDateLocal,
  generateSharePassword,
  maxExpiresDateLocal,
  minExpiresDateLocal,
  resolveShareExpiresAtIso,
  type ShareExpiresPreset,
} from "@/lib/answerShare/password";
import { getAnswerShareUrl } from "@/lib/answerShare/url";
import type { AnswerShareManageState } from "@/types/answerShare";

function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shareStatusLabel(state: AnswerShareManageState): string {
  if (!state.exists) return "Не создана";
  if (state.enabled === false) return "Выключена";
  if (state.expiresAt) {
    const expires = new Date(state.expiresAt);
    if (!Number.isNaN(expires.getTime()) && expires.getTime() <= Date.now()) {
      return "Истекла";
    }
  }
  return "Активна";
}

const EXPIRES_PRESET_OPTIONS: Array<{ id: ShareExpiresPreset; label: string }> =
  [
    { id: 1, label: "1 день" },
    { id: 7, label: "7 дней" },
    { id: 30, label: "30 дней" },
    { id: "custom", label: "Своя дата" },
  ];

function presetChipClass(active: boolean): string {
  return [
    "rounded-md px-2.5 py-1.5 font-onest text-xs font-medium transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30",
    active
      ? "bg-brand-500 text-white shadow-sm"
      : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
  ].join(" ");
}

function AnswerShareExpiresPicker({
  preset,
  customDate,
  onPresetChange,
  onCustomDateChange,
}: {
  preset: ShareExpiresPreset;
  customDate: string;
  onPresetChange: (preset: ShareExpiresPreset) => void;
  onCustomDateChange: (date: string) => void;
}) {
  const selectPreset = (next: ShareExpiresPreset) => {
    onPresetChange(next);
    if (next !== "custom") {
      onCustomDateChange(defaultExpiresDateLocal(next));
    }
  };

  return (
    <div className="space-y-2">
      <FieldLabel size="compact">Срок действия</FieldLabel>
      <div className="flex flex-wrap gap-1.5">
        {EXPIRES_PRESET_OPTIONS.map((option) => (
          <button
            key={String(option.id)}
            type="button"
            aria-pressed={preset === option.id}
            onClick={() => selectPreset(option.id)}
            className={presetChipClass(preset === option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>
      {preset === "custom" ? (
        <input
          id="answer-share-expires"
          type="date"
          value={customDate}
          min={minExpiresDateLocal()}
          max={maxExpiresDateLocal()}
          onChange={(event) => onCustomDateChange(event.target.value)}
          className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-onest text-sm text-slate-800 shadow-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
        />
      ) : (
        <p className="font-onest text-xs text-slate-500">
          До{" "}
          {formatDateTime(
            resolveShareExpiresAtIso(preset, customDate) ?? undefined,
          )}
        </p>
      )}
    </div>
  );
}

function resetExpiresForm(
  setPreset: (preset: ShareExpiresPreset) => void,
  setCustomDate: (date: string) => void,
) {
  setPreset(30);
  setCustomDate(defaultExpiresDateLocal(30));
}

interface AnswerShareSettingsProps {
  formId: number;
  canManage?: boolean;
  collapsible?: boolean;
}

export function AnswerShareSettings({
  formId,
  canManage = true,
  collapsible = false,
}: AnswerShareSettingsProps) {
  const [expanded, setExpanded] = useState(false);
  const [state, setState] = useState<AnswerShareManageState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState(() => generateSharePassword());
  const [expiresPreset, setExpiresPreset] = useState<ShareExpiresPreset>(30);
  const [customExpiresDate, setCustomExpiresDate] = useState(() =>
    defaultExpiresDateLocal(30),
  );
  const [creating, setCreating] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [lastCreatedPassword, setLastCreatedPassword] = useState<string | null>(
    null,
  );
  const [showCreateForm, setShowCreateForm] = useState(false);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await fetchAnswerShare(formId);
      setState(next);
      setShowCreateForm(!next.exists);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Не удалось загрузить настройки ссылки",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManage) return;
    void reload();
  }, [formId, canManage]);

  useEffect(() => {
    if (!copiedLink) return;
    const timer = window.setTimeout(() => setCopiedLink(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copiedLink]);

  useEffect(() => {
    if (!copiedPassword) return;
    const timer = window.setTimeout(() => setCopiedPassword(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copiedPassword]);

  const shareUrl =
    state?.exists && state.token ? getAnswerShareUrl(state.token) : null;

  const handleCreate = async () => {
    const expiresAt = resolveShareExpiresAtIso(
      expiresPreset,
      customExpiresDate,
    );
    if (!password.trim() || password.trim().length < 8) {
      setError("Пароль должен быть не короче 8 символов");
      return;
    }
    if (!expiresAt) {
      setError("Укажите корректную дату окончания");
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const next = await createAnswerShare(formId, {
        password: password.trim(),
        expiresAt,
      });
      setState(next);
      setLastCreatedPassword(password.trim());
      setShowCreateForm(false);
      setPassword(generateSharePassword());
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const first = err.errors
          ? Object.values(err.errors).flat().find(Boolean)
          : null;
        setError(first ?? err.message ?? "Проверьте пароль и дату");
      } else {
        setError(
          err instanceof ApiError ? err.message : "Не удалось создать ссылку",
        );
      }
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (enabled: boolean) => {
    setToggling(true);
    setError(null);
    try {
      const next = await patchAnswerShare(formId, { enabled });
      setState(next);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setError(
          err.message ||
            "Не удалось включить ссылку. Возможно, срок истёк — создайте новую.",
        );
        setShowCreateForm(true);
      } else {
        setError(
          err instanceof ApiError
            ? err.message
            : "Не удалось изменить статус ссылки",
        );
      }
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Удалить ссылку на ответы? Старый URL перестанет работать без возможности включить.",
      )
    ) {
      return;
    }

    setDeleting(true);
    setError(null);
    try {
      await deleteAnswerShare(formId);
      setState({ exists: false });
      setLastCreatedPassword(null);
      setShowCreateForm(true);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось удалить ссылку",
      );
    } finally {
      setDeleting(false);
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
    } catch {
      setError("Не удалось скопировать ссылку");
    }
  };

  const copyPassword = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedPassword(true);
    } catch {
      setError("Не удалось скопировать пароль");
    }
  };

  if (!canManage) {
    return (
      <p className="font-onest text-xs text-slate-500">
        Ссылка на ответы доступна менеджерам формы.
      </p>
    );
  }

  const isCreated = Boolean(state?.exists);
  const statusText =
    loading && !state ? "…" : state ? shareStatusLabel(state) : "Не создана";

  const panel = (
    <div className="space-y-4">
      {!collapsible && state && !loading ? (
        <p className="font-onest text-xs text-slate-500">
          {shareStatusLabel(state)}
          {state.expiresAt ? ` · до ${formatDateTime(state.expiresAt)}` : ""}
        </p>
      ) : null}

      {error ? (
        <div
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2"
          role="alert"
        >
          <p className="font-onest text-sm text-red-700">{error}</p>
        </div>
      ) : null}

      {loading && !state ? (
        <p className="font-onest text-sm text-slate-500">Загрузка…</p>
      ) : null}

      {state?.exists && shareUrl && !showCreateForm ? (
        <div className="space-y-4">
          <div>
            <FieldLabel size="compact">URL для гостя</FieldLabel>
            <div className="mt-1 flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
              <a
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 flex-1 truncate px-2 py-1.5 font-onest text-xs text-brand-700 hover:underline"
                title={shareUrl}
              >
                {shareUrl.replace(/^https?:\/\//, "")}
              </a>
              <button
                type="button"
                title={copiedLink ? "Скопировано" : "Копировать ссылку"}
                onClick={() => void copyLink()}
                className={[
                  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border transition-colors",
                  copiedLink
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                ].join(" ")}
              >
                {copiedLink ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </button>
            </div>
          </div>

          <ToggleSwitch
            id="answer-share-enabled"
            checked={state.enabled !== false}
            compact
            label="Ссылка включена"
            description="Выключение сразу закрывает доступ по уже выданным токенам"
            onChange={(checked) => {
              if (!toggling) void handleToggle(checked);
            }}
          />

          {lastCreatedPassword ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="font-onest text-xs font-medium text-amber-900">
                Сохраните пароль — он больше не будет показан
              </p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 rounded bg-white px-2 py-1 font-mono text-sm text-slate-800">
                  {lastCreatedPassword}
                </code>
                <Button
                  type="button"
                  variant="secondary"
                  className="px-3 py-1.5 text-sm"
                  onClick={() => void copyPassword(lastCreatedPassword)}
                >
                  {copiedPassword ? "Скопировано" : "Копировать"}
                </Button>
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            <Button
              type="button"
              variant="secondary"
              className="px-3 py-1.5 text-sm"
              onClick={() => {
                setShowCreateForm(true);
                setLastCreatedPassword(null);
                setPassword(generateSharePassword());
                resetExpiresForm(setExpiresPreset, setCustomExpiresDate);
              }}
            >
              Новая ссылка
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="px-3 py-1.5 text-sm text-red-700 hover:border-red-200 hover:bg-red-50"
              disabled={deleting}
              onClick={() => void handleDelete()}
            >
              {deleting ? "Удаление…" : "Удалить"}
            </Button>
          </div>
        </div>
      ) : null}

      {showCreateForm ? (
        <div className="space-y-3">
          <p className="font-onest text-xs text-slate-600">
            {state?.exists
              ? "Новая ссылка заменит текущую. Пароль задаёте вы — в URL его нет."
              : "Гость откроет ссылку и введёт пароль. Только просмотр и экспорт ответов."}
          </p>
          <div>
            <FieldLabel htmlFor="answer-share-password" size="compact">
              Пароль (8–72 символа)
            </FieldLabel>
            <div className="mt-1 flex gap-2">
              <TextInput
                id="answer-share-password"
                type="text"
                size="compact"
                value={password}
                onChange={setPassword}
                className="font-mono"
              />
              <Button
                type="button"
                variant="secondary"
                className="shrink-0 px-3 py-1.5 text-sm"
                onClick={() => setPassword(generateSharePassword())}
              >
                Сгенерировать
              </Button>
            </div>
          </div>
          <AnswerShareExpiresPicker
            preset={expiresPreset}
            customDate={customExpiresDate}
            onPresetChange={setExpiresPreset}
            onCustomDateChange={(date) => {
              setCustomExpiresDate(date);
              setExpiresPreset("custom");
            }}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              className="px-4 py-2 text-sm"
              disabled={creating}
              onClick={() => void handleCreate()}
            >
              {creating
                ? "Сохранение…"
                : state?.exists
                  ? "Заменить ссылку"
                  : "Создать ссылку"}
            </Button>
            {state?.exists ? (
              <Button
                type="button"
                variant="secondary"
                className="px-4 py-2 text-sm"
                onClick={() => setShowCreateForm(false)}
              >
                Отмена
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );

  if (!collapsible) {
    return panel;
  }

  return (
    <div>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
        className={[
          "flex w-full items-center gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors",
          isCreated
            ? "border-emerald-200 bg-emerald-50/80 text-emerald-900 hover:bg-emerald-50"
            : "border-slate-200 bg-slate-50/50 text-slate-400 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-500",
        ].join(" ")}
      >
        <span
          className={[
            "min-w-0 flex-1 font-onest text-sm font-medium",
            isCreated ? "text-emerald-900" : "text-slate-500",
          ].join(" ")}
        >
          Ссылка на ответы
        </span>
        {!loading ? (
          <span
            className={[
              "shrink-0 font-onest text-xs",
              isCreated ? "text-emerald-700/80" : "text-slate-400",
            ].join(" ")}
          >
            {statusText}
          </span>
        ) : null}
        <IconChevronDown
          size={16}
          className={[
            "shrink-0 transition-transform",
            expanded ? "rotate-180" : "",
            isCreated ? "text-emerald-700" : "text-slate-400",
          ].join(" ")}
        />
      </button>
      {expanded ? <div className="pt-4">{panel}</div> : null}
    </div>
  );
}

export { shareStatusLabel };
